import { HugeiconsIcon } from "@hugeicons/react";
import { PlusSignSquareIcon } from "@hugeicons-pro/core-duotone-rounded";
import {
	MoreHorizontalCircle01Icon,
	PodcastIcon,
} from "@hugeicons-pro/core-solid-rounded";
import {
	AddSquareIcon,
	Mic01Icon as EpisodeIconDutotone,
	Calendar01Icon as PlanningIconDutotone,
	Settings05Icon as SettingsIconDutotone,
	AnalyticsUpIcon as StatisticsIconDutotone,
} from "@hugeicons-pro/core-stroke-rounded";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ComponentProps } from "react";
import { AppContainer } from "~/components/layouts/app/app-container";
import { SourceTypeBadge } from "~/components/source-type-badge";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "~/components/ui/card";
import { CardLoadingIndicator } from "~/components/ui/card-loading-indicator";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { PageHead } from "~/components/ui/page-head";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import {
	type Channel,
	ChannelStatus,
	type ChannelStatusValue,
	getThemeColor,
} from "~/models/channel";
import { useTRPC } from "~/trpc/client";

export const Route = createFileRoute("/_app/manager/")({
	component: Page,
	async loader({ context }) {
		context.queryClient.prefetchQuery(context.trpc.channel.list.queryOptions());
	},
});

function Page() {
	const trpc = useTRPC();
	const { data: channels, isFetching } = useQuery(
		trpc.channel.list.queryOptions(),
	);

	return (
		<>
			<PageHead
				icon={PodcastIcon}
				title="Podcasts"
				subtitle="Quick overview of all your shows."
				action={
					<Button asChild className="w-fit" variant="outline">
						<Link to="/manager/new">
							<HugeiconsIcon
								icon={AddSquareIcon}
								aria-hidden="true"
								className="size-4"
							/>
							<Trans>Create or Import a Podcast</Trans>
						</Link>
					</Button>
				}
			/>
			<AppContainer className="my-12">
				<Card>
					<CardHeader>
						<CardTitle className="text-xl">
							<Trans>All your podcasts</Trans>
						</CardTitle>
						<CardDescription>
							<Trans>Quick overview of all your shows.</Trans>
						</CardDescription>
					</CardHeader>
					<CardContent className="relative grid gap-6 items-start justify-start grid-cols-[repeat(auto-fit,minmax(240px,0.25fr))] min-h-40">
						{channels?.map((channel) => (
							<ChannelCard key={channel.uid} channel={channel} />
						))}
						{isFetching && <CardLoadingIndicator />}
						{!isFetching && (!channels || channels.length === 0) && (
							<div className="h-full w-full flex items-center justify-center flex-col gap-4 col-span-full">
								<div className="bg-muted size-10 rounded-md border flex items-center justify-center">
									<HugeiconsIcon
										icon={PlusSignSquareIcon}
										className="size-6 text-muted-foreground"
									/>
								</div>
								<h2 className="font-medium">
									<Trans>Create your first podcast</Trans>
								</h2>
								<p className="text-sm text-secondary-foreground">
									<Trans>
										It seems you don't have any podcasts yet. Do you want to
										create or import a new one?
									</Trans>
								</p>
								<Button asChild className="w-fit" size="lg">
									<Link to="/manager/new">
										<Trans>Create or Import a Podcast</Trans>
									</Link>
								</Button>
							</div>
						)}
					</CardContent>
				</Card>
			</AppContainer>
		</>
	);
}

function ChannelCard({ channel }: { channel: Channel }) {
	const isAvailable = ![
		ChannelStatus.Removed_TS,
		ChannelStatus.Removed,
	].includes(channel.statusID);

	return (
		<div
			className={cn("group relative p-4 rounded-md", {
				"bg-accent/30 shadow-xs-with-border": isAvailable,
				"bg-black/10": !isAvailable,
			})}
		>
			<div className="size-full shrink-0 overflow-hidden rounded-md ring-1 ring-black/[0.08] dark:ring-white/[0.08] ring-inset transition">
				{isAvailable ? (
					<Link to="/podcasts/$channelUid" params={{ channelUid: channel.uid }}>
						<ChannelAvatar channel={channel} />
					</Link>
				) : (
					<ChannelAvatar channel={channel} />
				)}
			</div>
			<div className="mt-4 flex items-center gap-2">
				<div className="truncate">
					{isAvailable ? (
						<Link
							to="/podcasts/$channelUid"
							params={{ channelUid: channel.uid }}
						>
							<h2 className="truncate font-medium leading-none hover:underline">
								{channel.title}
							</h2>
						</Link>
					) : (
						<h2 className="truncate font-medium leading-none hover:underline">
							{channel.title}
						</h2>
					)}
					<p className="text-sm text-muted-foreground truncate">
						{(channel.author ?? "").trim().length > 0 ? channel.author : "—"}
					</p>
					<SourceTypeBadge sourceTypeID={channel.sourceTypeID} />
				</div>
				{isAvailable && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild className="ml-auto shrink-0">
							<Button variant="ghost" size="icon">
								<span className="sr-only">
									<Trans>Open menu</Trans>
								</span>
								<HugeiconsIcon icon={MoreHorizontalCircle01Icon} />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem asChild>
								<Link
									to="/podcasts/$channelUid/episodes"
									params={{
										channelUid: channel.uid,
									}}
								>
									<HugeiconsIcon icon={EpisodeIconDutotone} />
									<Trans>Episodes</Trans>
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<Link
									to="/podcasts/$channelUid/statistics"
									params={{
										channelUid: channel.uid,
									}}
								>
									<HugeiconsIcon icon={StatisticsIconDutotone} />
									<Trans>Statistics</Trans>
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<Link
									to="/podcasts/$channelUid/statistics"
									params={{
										channelUid: channel.uid,
									}}
								>
									<HugeiconsIcon icon={PlanningIconDutotone} />
									<Trans>Planning</Trans>
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<Link
									to="/podcasts/$channelUid/settings"
									params={{
										channelUid: channel.uid,
									}}
								>
									<HugeiconsIcon icon={SettingsIconDutotone} />
									<Trans>Settings</Trans>
								</Link>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>
			<ChannelStatusIndicator
				statusID={channel.statusID}
				className="absolute top-1.5 right-1.5"
			/>
		</div>
	);
}

function ChannelAvatar({ channel }: { channel: Channel }) {
	return (
		<Avatar className="size-full rounded">
			<AvatarImage src={channel.iconsAssetURL} alt={channel.title} />
			<AvatarFallback
				color={getThemeColor(channel.themeColor)}
				className="size-full aspect-square rounded text-7xl"
			>
				{channel.title.slice(0, 2)}
			</AvatarFallback>
		</Avatar>
	);
}

function ChannelStatusIndicator({
	className,
	statusID,
	...props
}: ComponentProps<"div"> & {
	statusID: ChannelStatusValue;
}) {
	switch (true) {
		case [ChannelStatus.Removed_TS, ChannelStatus.Removed].includes(statusID):
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div
							className={cn(
								"rounded-full bg-red-500/20 p-2 animate-pulse",
								className,
							)}
							{...props}
						>
							<div className="size-3 bg-red-500 rounded-full" />
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p className="text-xs font-semibold text-muted-foreground">
							<Trans>Removed</Trans>
						</p>
					</TooltipContent>
				</Tooltip>
			);
		case [ChannelStatus.Active, ChannelStatus.Active_B2B].includes(statusID):
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div
							className={cn(
								"rounded-full bg-emerald-500/20 p-2 animate-pulse",
								className,
							)}
							{...props}
						>
							<div className="size-3 bg-emerald-500 rounded-full" />
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p className="text-xs font-semibold text-muted-foreground">
							<Trans>Active</Trans>
						</p>
					</TooltipContent>
				</Tooltip>
			);
		case [ChannelStatus.Frozen].includes(statusID):
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div
							className={cn("rounded-full bg-gray-500/20 p-2", className)}
							{...props}
						>
							<div className="size-3 bg-gray-500 rounded-full" />
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p className="text-xs font-semibold text-muted-foreground">
							<Trans>Frozen</Trans>
						</p>
					</TooltipContent>
				</Tooltip>
			);
		case [ChannelStatus.Incomplete].includes(statusID):
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div
							className={cn("rounded-full bg-amber-500/20 p-2", className)}
							{...props}
						>
							<div className="size-3 bg-amber-500 rounded-full" />
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p className="text-xs font-semibold text-muted-foreground">
							<Trans>Incomplete</Trans>
						</p>
					</TooltipContent>
				</Tooltip>
			);
		case [ChannelStatus.Rss_Syncing].includes(statusID):
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div
							className={cn(
								"rounded-full bg-orange-500/20 p-2 animate-pulse",
								className,
							)}
							{...props}
						>
							<div className="size-3 bg-orange-500 rounded-full" />
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p className="text-xs font-semibold text-muted-foreground">
							<Trans>RSS Syncing</Trans>
						</p>
					</TooltipContent>
				</Tooltip>
			);
		case [ChannelStatus.ReTranscode].includes(statusID):
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<div
							className={cn(
								"rounded-full bg-indigo-500/20 p-2 animate-pulse",
								className,
							)}
							{...props}
						>
							<div className="size-3 bg-indigo-500 rounded-full" />
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p className="text-xs font-semibold text-muted-foreground">
							<Trans>Transcoding</Trans>
						</p>
					</TooltipContent>
				</Tooltip>
			);
		default:
			return null;
	}
}
